// Generic, source-agnostic List view (ADR D3/P0.4).
//
// Renders any TaskRecord[] through the unified ViewState engine: filter → sort →
// bucket, then collapsible sections of RecordRows. Used by the cross-project
// "Meine Aufgaben" hub and personal Lists; the project page adopts it for its
// non-phase (bucketed) grouping modes.
//
// DnD (#876 A): rows can be (a) reordered within a group when sort is
// "manual", persisting the new position, (b) dropped into a different
// group to change whatever field the list is grouped by — reusing the exact
// same columnChange/resolvePhaseMove/resolveTypeMove drop-mapping as
// TaskBoardView so the two surfaces can't drift apart — and (c) dropped onto a
// list in the sidebar to move a personal task into that list (store/listDrop,
// which bridges the two separate DragDropContexts).
//
// ClickUp-style group headers: a colored tinted band with the group's color,
// an UPPERCASE name (for status/priority/due groups), a count pill, and a
// persistent low-emphasis "+ Aufgabe" inline add row per group.

import { useEffect, useState } from "preact/hooks";
import { ChevronDown, ChevronRight, ListTodo, Plus } from "lucide-preact";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import type { DragStart, DropResult } from "@hello-pangea/dnd";
import { RecordRow } from "../components/RecordRow";
import { RecordStepList } from "../components/RecordStepList";
import { batchUpdateTasks, moveTasksToPhase } from "../lib/api";
import { reorderTask } from "../store/project";
import { updatePersonalTask } from "../store/personalTasks";
import {
  beginListDrop,
  endListDrop,
  isListDropCandidate,
  moveTaskToList,
} from "../store/listDrop";
import type { ResolverContext, TaskRecord } from "../lib/taskRecord";
import {
  columnChange,
  DND_CAPABLE_GROUPINGS,
  resolvePhaseMove,
  resolveTypeMove,
} from "./TaskBoardView";
import { computeView, type Bucket, type BucketOptions, type ViewState } from "../lib/viewState";
import { displayOrder } from "../lib/taskDetailModel";

interface Props {
  records: TaskRecord[];
  state: ViewState;
  ctx: ResolverContext;
  /** Resolver/order options for grouping (statuses, phases, user/project resolvers, today). */
  bucketOpts?: BucketOptions;
  readOnly?: boolean;
  onOpenRecord?: (record: TaskRecord) => void;
  /** Delete a single record — surfaces a hover trash button on each row. */
  onDeleteRecord?: (record: TaskRecord) => void | Promise<void>;
  /** Selected record uids (bulk select). */
  selectedUids?: Set<string>;
  onToggleSelect?: (uid: string) => void;
  /** Empty-state copy when there are no records at all. */
  emptyLabel?: string;
  /**
   * Add a task inside a group. `bucket` carries the group's key/label so the
   * caller can derive defaults (group-by-status → that status_id, etc.).
   * When omitted, the inline add row is not rendered.
   */
  onAddInGroup?: (bucket: Bucket, title: string) => void | Promise<void>;
  /**
   * UX-Vorschlag P3: meldet die angezeigte Reihenfolge (Gruppen × Sortierung,
   * ohne eingeklappte Gruppen), damit ↑/↓ im Detailbereich ihr folgt.
   * Optional — ohne Aufrufer aendert sich nichts.
   */
  onDisplayOrder?: (uids: string[]) => void;
}

/** Group names rendered UPPERCASE (ClickUp signature) — proper nouns stay as-is. */
const UPPERCASE_GROUPS = new Set(["status", "priority", "due"]);

/** Inline "+ Aufgabe" row at the bottom of a group. Owns its own input state. */
function GroupAddRow(props: {
  color: string;
  onAdd: (title: string) => void | Promise<void>;
}): preact.JSX.Element {
  const [value, setValue] = useState("");
  const submit = (): void => {
    const t = value.trim();
    if (!t) return;
    setValue("");
    // Restore the typed title if creating fails, so the input isn't lost.
    void Promise.resolve(props.onAdd(t)).catch(() => setValue(t));
  };
  return (
    <div class="flex items-center gap-2 px-3 py-2 border-t border-[color:var(--color-outline-variant)]/40 focus-within:shadow-[inset_3px_0_0_var(--color-primary)]">
      <Plus size={14} class="shrink-0 text-[color:var(--color-on-surface-variant)]" aria-hidden="true" />
      <input
        type="text"
        value={value}
        onInput={(e) => setValue((e.currentTarget as HTMLInputElement).value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          }
        }}
        onBlur={submit}
        placeholder="Aufgabe hinzufügen…"
        aria-label="Aufgabe in dieser Gruppe hinzufügen"
        class="flex-1 min-w-0 bg-transparent text-base sm:text-sm text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none"
      />
    </div>
  );
}

export function TaskListView(props: Props): preact.JSX.Element {
  const {
    records,
    state,
    ctx,
    bucketOpts,
    readOnly = false,
    selectedUids,
    onToggleSelect,
    emptyLabel = "Keine Aufgaben",
    onAddInGroup,
    onDisplayOrder,
  } = props;

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggle = (key: string): void =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Rows whose inline step list is currently expanded (keyed by record uid).
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpand = (uid: string): void =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });

  const buckets = computeView(records, state, bucketOpts);
  const anySelected = (selectedUids?.size ?? 0) > 0;
  // Only show the project chip on rows when not already grouped by project.
  const showProject = state.groupBy !== "project";
  // "none" grouping → a single flat list with no section chrome.
  const flat = state.groupBy === "none";
  const uppercase = UPPERCASE_GROUPS.has(state.groupBy);

  const shownOrder = onDisplayOrder ? displayOrder(buckets, collapsed, flat) : [];
  const shownKey = shownOrder.join(",");
  useEffect(() => {
    onDisplayOrder?.(shownOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownKey]);

  // ---------- drag & drop (#876 A) ----------
  //
  // A drop is meaningful in two independent ways:
  //  1. Reordering within a group — only persistable when sort is "manual":
  //     that's the only sort key that reads position (sortOrder) rather than
  //     recomputing order from other fields on every render. Under any other
  //     sort, a same-group drop wouldn't survive a reload, so it's a no-op —
  //     the row visually snaps back next render since we never touch local
  //     state to "hold" it in place. (Alternative considered: force-switch to
  //     manual sort on such a drop — rejected because it would silently change
  //     the user's chosen sort as a side effect of an unrelated gesture.)
  //  2. Dropping into a *different* group to set whatever field the list is
  //     grouped by — independent of sort, and reusing the exact same
  //     columnChange/resolvePhaseMove/resolveTypeMove drop-mapping as
  //     TaskBoardView so list and board can't drift apart.
  //  3. Dropping onto a list in the sidebar to move a personal task into that
  //     list — independent of grouping and sort, so the gesture is available
  //     even in a flat, date-sorted single list. Handled by store/listDrop.
  const isManualSort = state.sort[0]?.key === "manual";
  const bucketDroppable = DND_CAPABLE_GROUPINGS.has(state.groupBy);
  // A personal row is draggable even when neither in-view drop would do
  // anything — otherwise the sidebar move would be unreachable under exactly
  // the groupings/sorts where it is most useful.
  const listDroppable = records.some(isListDropCandidate);
  const dndEnabled = !readOnly && (isManualSort || bucketDroppable || listDroppable);
  /** Per-row gate: an in-view drop (reorder / group change) applies to any row,
   *  the sidebar-list drop only to movable personal tasks — so e.g. a project
   *  row in the cross-source hub stays non-draggable under a grouping that
   *  can't be dropped into, instead of offering an inert gesture. */
  const rowDraggable = (r: TaskRecord): boolean =>
    dndEnabled && (isManualSort || bucketDroppable || isListDropCandidate(r));

  const byUid = new Map<string, TaskRecord>();
  for (const b of buckets) for (const r of b.items) byUid.set(r.uid, r);

  /** Persist a manual-sort reorder — project tasks via the phase/sort_order
   *  reorder endpoint, personal tasks via their plain sort_order patch field
   *  (personal tasks have no dedicated reorder endpoint / sibling-shifting). */
  const persistReorder = (rec: TaskRecord, index: number): void => {
    if (rec.source === "project") void reorderTask(rec.id, { sort_order: index });
    else void updatePersonalTask(rec.id, { sort_order: index });
  };

  const onDragStart = (start: DragStart): void => {
    const rec = byUid.get(start.draggableId);
    if (rec && isListDropCandidate(rec)) beginListDrop(rec.listId);
  };

  const onDragEnd = (result: DropResult): void => {
    const { source, destination, draggableId } = result;
    // Always ends the pointer tracking, also for drags that never started it.
    const listTarget = endListDrop();
    const rec = byUid.get(draggableId);
    // A sidebar list under the pointer wins over any in-view destination: the
    // pointer is what the user aimed with, while hello-pangea derives
    // `destination` from the dragged clone's centre, which can still overlap
    // the content area. "CANCEL" (Escape / programmatic abort) must not move.
    if (result.reason === "DROP" && listTarget !== null && rec && isListDropCandidate(rec)) {
      void moveTaskToList(rec.id, listTarget);
      return;
    }
    if (!destination) return;
    const destKey = destination.droppableId.slice("list-bucket-".length);
    const srcKey = source.droppableId.slice("list-bucket-".length);
    // isDragDisabled already blocks sub-task drags at the gesture level; this
    // is defense in depth against a stale draggableId.
    if (!rec || rec.parentId !== null) return;
    if (destKey === srcKey) {
      if (destination.index !== source.index && isManualSort) persistReorder(rec, destination.index);
      return;
    }
    // Cross-group move — phase/type can't be expressed as a TaskRecordChange
    // (taskRecord.ts is out of scope to extend), so they bypass ctx.patch.
    if (destKey.startsWith("phase:")) {
      const phaseId = resolvePhaseMove(destKey, rec);
      if (phaseId !== null) void moveTasksToPhase([rec.id], phaseId);
      return;
    }
    if (destKey.startsWith("type:")) {
      const typeId = resolveTypeMove(destKey, rec);
      if (typeId !== null) void batchUpdateTasks({ ids: [rec.id], patch: { type_id: typeId } });
      return;
    }
    const change = columnChange(destKey, rec, ctx);
    if (change) void ctx.patch(rec, change);
  };

  if (records.length === 0) {
    return (
      <div class="rounded border border-dashed border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-4 py-12 text-center">
        <ListTodo size={24} class="mx-auto text-[color:var(--color-on-surface-variant)]/50" aria-hidden="true" />
        <p class="mt-2 text-sm font-medium text-[color:var(--color-on-surface)]">{emptyLabel}</p>
      </div>
    );
  }

  const renderRows = (items: TaskRecord[]): preact.JSX.Element[] =>
    items.map((r, idx) => {
      // Steps expansion is a personal-task affordance only; project rows (e.g.
      // in the cross-project hub) open in their own modal instead.
      const stepsExpandable = r.source === "personal";
      const isExpanded = expanded.has(r.uid);
      const content = (
        <>
          <RecordRow
            record={r}
            ctx={ctx}
            hiddenColumns={state.hiddenColumns}
            showProject={showProject}
            readOnly={readOnly}
            selected={selectedUids?.has(r.uid)}
            onSelect={onToggleSelect}
            anySelected={anySelected}
            onOpen={props.onOpenRecord}
            onDelete={props.onDeleteRecord}
            onToggleExpand={stepsExpandable ? toggleExpand : undefined}
            expanded={isExpanded}
          />
          {stepsExpandable && isExpanded && (
            <RecordStepList taskId={r.id} readOnly={readOnly} />
          )}
        </>
      );
      // RecordRow has no dragHandleProps passthrough (unlike RecordCard), so
      // the whole row is made draggable via an outer wrapper — same "whole
      // card is the handle" convention as the board, touch-capable via
      // @hello-pangea/dnd. Sub-tasks stay non-draggable, consistent with
      // TableView (they must follow their parent).
      return (
        <Draggable key={r.uid} draggableId={r.uid} index={idx} isDragDisabled={!rowDraggable(r) || r.parentId !== null}>
          {(dragProv, dragSnap) => (
            <div
              ref={dragProv.innerRef}
              {...(dragProv.draggableProps as unknown as Record<string, unknown>)}
              {...((dragProv.dragHandleProps ?? {}) as unknown as Record<string, unknown>)}
              // The drag handle props add a tabIndex/role for hello-pangea's
              // own keyboard-drag protocol; RecordRow already has its own
              // role="button"/tabIndex for opening the detail, so suppress
              // the wrapper's tab stop to avoid doubling them up.
              tabIndex={-1}
              style={dragProv.draggableProps.style as preact.JSX.CSSProperties | undefined}
              class={dragSnap.isDragging ? "shadow-level-2 relative z-10" : ""}
            >
              {content}
            </div>
          )}
        </Draggable>
      );
    });

  if (flat) {
    const bucket0 = buckets[0];
    const items = bucket0?.items ?? [];
    return (
      <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <div class="rounded border border-[color:var(--color-outline-variant)] overflow-hidden bg-[color:var(--color-surface-container-lowest)]">
          {items.length === 0 ? (
            <p class="px-4 py-6 text-center text-xs text-[color:var(--color-on-surface-variant)] italic">
              Keine Aufgaben passen zum Filter
            </p>
          ) : (
            <Droppable droppableId={`list-bucket-${bucket0?.key ?? "all"}`} isDropDisabled={!dndEnabled}>
              {(prov) => (
                <div ref={prov.innerRef} {...prov.droppableProps}>
                  {renderRows(items)}
                  {prov.placeholder}
                </div>
              )}
            </Droppable>
          )}
          {onAddInGroup && !readOnly && bucket0 && (
            <GroupAddRow color="var(--color-primary)" onAdd={(t) => onAddInGroup(bucket0, t)} />
          )}
        </div>
      </DragDropContext>
    );
  }

  return (
    <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div class="flex flex-col gap-3">
        {buckets.map((b) => {
          const isCollapsed = collapsed.has(b.key);
          const doneCount = b.items.filter((r) => r.done).length;
          // Gruppenkopf wie der Phasenkopf der Tabelle: neutrale Flaeche,
          // Farbe nur als Randstreifen + Punkt (Farbe = Bedeutung, nicht
          // Dekoration — KINETIC-FLOW-SYSTEM.md). Vorher war das ganze Band
          // eingefaerbt und der Name in Gruppenfarbe gesetzt; rot getoente
          // Projektkoepfe lasen sich wie Fehlermeldungen.
          return (
            <section key={b.key} class="flex flex-col">
              <div
                class="flex min-w-0 items-center gap-2 px-3 py-2 rounded-t-md border border-b-0 border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)]"
                style={{ borderLeft: `4px solid ${b.color}` }}
              >
                <button
                  type="button"
                  onClick={() => toggle(b.key)}
                  class="inline-flex shrink-0 items-center justify-center size-6 rounded text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-on-surface)]/10 hover:text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                  aria-expanded={!isCollapsed}
                  aria-label={isCollapsed ? `Gruppe „${b.label || "Alle"}" einblenden` : `Gruppe „${b.label || "Alle"}" ausblenden`}
                >
                  {isCollapsed ? <ChevronRight size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
                </button>
                <span
                  class="size-2 shrink-0 rounded-full"
                  style={{ background: b.color }}
                  aria-hidden="true"
                />
                <h2
                  class={`min-w-0 truncate text-sm font-semibold text-[color:var(--color-on-surface)] ${uppercase ? "text-xs uppercase tracking-wide" : ""}`}
                  title={b.label || "Alle"}
                >
                  {b.label || "Alle"}
                </h2>
                <span
                  class="inline-flex shrink-0 items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-micro font-semibold tabular-nums bg-[color:var(--color-surface-container-high)] text-[color:var(--color-on-surface-variant)]"
                  title={`${b.items.length} ${b.items.length === 1 ? "Aufgabe" : "Aufgaben"}${doneCount > 0 ? ` · ${doneCount} erledigt` : ""}`}
                >
                  {b.items.length}
                </span>
              </div>
              {!isCollapsed && (
                <div class="rounded-b-md border border-t-0 border-[color:var(--color-outline-variant)] overflow-hidden bg-[color:var(--color-surface-container-lowest)]">
                  {b.items.length === 0 ? (
                    // Empty groups still need a mounted Droppable, or dragging
                    // a row into an empty bucket would have nowhere to land.
                    <Droppable droppableId={`list-bucket-${b.key}`} isDropDisabled={!dndEnabled}>
                      {(prov) => (
                        <div ref={prov.innerRef} {...prov.droppableProps}>
                          <p class="px-4 py-3 text-xs text-[color:var(--color-on-surface-variant)] italic">
                            Keine Aufgaben in dieser Gruppe
                          </p>
                          {prov.placeholder}
                        </div>
                      )}
                    </Droppable>
                  ) : (
                    <Droppable droppableId={`list-bucket-${b.key}`} isDropDisabled={!dndEnabled}>
                      {(prov) => (
                        <div ref={prov.innerRef} {...prov.droppableProps}>
                          {renderRows(b.items)}
                          {prov.placeholder}
                        </div>
                      )}
                    </Droppable>
                  )}
                  {onAddInGroup && !readOnly && (
                    <GroupAddRow color={b.color} onAdd={(t) => onAddInGroup(b, t)} />
                  )}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </DragDropContext>
  );
}
